import React, { useState, useCallback } from 'react';
import { functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { STokenLogo } from '../StreamDisplay';
import './ScratchCard.css';

const COST = 50;

// All possible winning symbols (ordered rarest → most common)
const SYMBOLS = [
  { emoji: '👑', label: 'Crown',    rarity: 'Ultimate',  reward: 10_000_000, color: '#ffffff', glowColor: 'rgba(255,255,255,0.9)' },
  { emoji: '💎', label: 'Diamond',  rarity: 'Legendary', reward: 250_000,    color: '#ffaa00', glowColor: 'rgba(255,170,0,0.8)' },
  { emoji: '⭐', label: 'Star',     rarity: 'Epic',      reward: 5_000,      color: '#bf5fff', glowColor: 'rgba(191,95,255,0.7)' },
  { emoji: '🔷', label: 'Gem',      rarity: 'Rare',      reward: 500,        color: '#3498db', glowColor: 'rgba(52,152,219,0.6)' },
  { emoji: '🟩', label: 'Square',   rarity: 'Uncommon',  reward: 200,        color: '#2ecc71', glowColor: 'rgba(46,204,113,0.5)' },
  { emoji: '📦', label: 'Box',      rarity: 'Common',    reward: 100,        color: '#95a5a6', glowColor: 'rgba(149,165,166,0.4)' },
];

// Filler symbol (never wins)
const MISS = { emoji: '❌', label: 'Miss', rarity: 'Miss', reward: 0, color: '#555', glowColor: 'transparent' };

// Outcome probability table — cumulative thresholds from rarest upward.
// These are exact probabilities of drawing a winning row for each tier.
//   Ultimate:  0.00000000000000001 % = 1e-19
//   Legendary: 0.00001 %             = 1e-7
//   Epic:      0.005 %               = 5e-5
//   Rare:      1.5 %                 = 0.015
//   Uncommon:  9 %                   = 0.09
//   Common:    35 %                  = 0.35
//   Lose:      remainder             ≈ 54.5 %
const OUTCOME_TABLE = [
  { symbolIdx: 0, cumProb: 1e-19    },  // Ultimate
  { symbolIdx: 1, cumProb: 1e-7     },  // Legendary
  { symbolIdx: 2, cumProb: 5e-5     },  // Epic
  { symbolIdx: 3, cumProb: 0.0150   },  // Rare  (+0.015 above Epic threshold)
  { symbolIdx: 4, cumProb: 0.1050   },  // Uncommon
  { symbolIdx: 5, cumProb: 0.4550   },  // Common
  { symbolIdx: -1, cumProb: 1.0     },  // Lose
];

// Pick a random filler symbol that is NOT the winner (avoids accidental extra match rows)
function randomFiller(excludeEmoji) {
  const fillers = [MISS, MISS, MISS, ...SYMBOLS.slice(3)]; // skew toward misses
  let pick;
  do { pick = fillers[Math.floor(Math.random() * fillers.length)]; }
  while (pick.emoji === excludeEmoji);
  return pick;
}

// Determine outcome and build the 9-cell grid in one step
function generateGrid() {
  const roll = Math.random();

  // Find which tier we hit
  let winSymbol = null;
  for (const entry of OUTCOME_TABLE) {
    if (roll < entry.cumProb) {
      winSymbol = entry.symbolIdx >= 0 ? SYMBOLS[entry.symbolIdx] : null;
      break;
    }
  }

  let cells;
  if (winSymbol) {
    // Row 0 = 3 matching winners; rows 1+2 = random non-matching fillers
    cells = [
      winSymbol, winSymbol, winSymbol,
      randomFiller(winSymbol.emoji), randomFiller(winSymbol.emoji), randomFiller(winSymbol.emoji),
      randomFiller(winSymbol.emoji), randomFiller(winSymbol.emoji), randomFiller(winSymbol.emoji),
    ];
    // Shuffle rows 1 & 2 to randomise row positions slightly (but never break row 0)
    // row 0 stays at indices 0-2; swap row 1 & 2 randomly
    if (Math.random() < 0.5) {
      [cells[3], cells[4], cells[5], cells[6], cells[7], cells[8]] =
      [cells[6], cells[7], cells[8], cells[3], cells[4], cells[5]];
    }
    // Also randomly move winning row to row 1 or 2 (40% of the time) for variety
    const winRow = Math.floor(Math.random() * 3);
    if (winRow !== 0) {
      const r0s = 0;
      const rWs = winRow * 3;
      for (let i = 0; i < 3; i++) {
        [cells[r0s + i], cells[rWs + i]] = [cells[rWs + i], cells[r0s + i]];
      }
    }
  } else {
    // No win — fill with random non-matching grid
    let attempt = 0;
    do {
      cells = Array.from({ length: 9 }, () => {
        const r = Math.random();
        // Heavily weight toward misses so it doesn't accidentally form a row
        if (r < 0.55) return MISS;
        const idx = Math.floor(Math.random() * SYMBOLS.length);
        return SYMBOLS[idx];
      });
      attempt++;
    } while (attempt < 30 && hasAnyWinRow(cells));
  }

  return { cells, totalReward: winSymbol ? winSymbol.reward : 0 };
}

function hasAnyWinRow(cells) {
  for (let row = 0; row < 3; row++) {
    const a = cells[row * 3], b = cells[row * 3 + 1], c = cells[row * 3 + 2];
    if (a.emoji === b.emoji && b.emoji === c.emoji && a.emoji !== '❌') return true;
  }
  return false;
}

function getWinningCells(cells) {
  const winners = new Set();
  for (let row = 0; row < 3; row++) {
    const a = cells[row * 3], b = cells[row * 3 + 1], c = cells[row * 3 + 2];
    if (a.emoji === b.emoji && b.emoji === c.emoji && a.emoji !== '❌') {
      winners.add(row * 3); winners.add(row * 3 + 1); winners.add(row * 3 + 2);
    }
  }
  return winners;
}


export default function ScratchCard({ onClose }) {
  const [phase, setPhase]       = useState('buy');   // 'buy' | 'scratching' | 'done'
  const [grid, setGrid]         = useState(null);    // { cells, totalReward }
  const [revealed, setRevealed] = useState(new Set());
  const [winCells, setWinCells] = useState(new Set());
  const [purchasing, setPurchasing] = useState(false);

  const buyCard = useCallback(() => {
    if (purchasing) return;
    setPurchasing(true);
    const g = generateGrid();
    setGrid(g);
    setRevealed(new Set());
    setWinCells(new Set());
    setPhase('scratching');
    setPurchasing(false);
  }, [purchasing]);

  const finalize = useCallback((cells, totalReward) => {
    const wc = getWinningCells(cells);
    setWinCells(wc);
    setPhase('done');
    httpsCallable(functions, 'creditGameResult')({ game: 'scratch', netStreams: totalReward - COST })
      .catch(e => console.error('[Scratch]', e));
  }, []);

  const revealCell = useCallback((index) => {
    if (phase !== 'scratching' || revealed.has(index) || !grid) return;
    const next = new Set(revealed);
    next.add(index);
    setRevealed(next);
    if (next.size === 9) finalize(grid.cells, grid.totalReward);
  }, [phase, revealed, grid, finalize]);

  const revealAll = useCallback(() => {
    if (!grid) return;
    setRevealed(new Set(Array.from({ length: 9 }, (_, i) => i)));
    finalize(grid.cells, grid.totalReward);
  }, [grid, finalize]);

  const newCard = useCallback(() => {
    setPhase('buy');
    setGrid(null);
    setRevealed(new Set());
    setWinCells(new Set());
  }, []);

  // ── BUY SCREEN ──────────────────────────────────────────────────────────────
  if (phase === 'buy') {
    return (
      <div className="scratch">
        <h2 className="scratch__title">Scratch Card</h2>
        <p className="scratch__cost" style={{ display:'flex', alignItems:'center', gap:4 }}>
          {COST}<STokenLogo size={13} /> / card · Match 3 in a row to win
        </p>

        {/* Prize table */}
        <div className="scratch__prizes">
          <div className="scratch__prizes-heading">All Prizes</div>
          {SYMBOLS.map(s => (
            <div key={s.emoji} className="scratch__prize-row" style={{ '--sym-color': s.color }}>
              <span className="scratch__prize-emoji">{s.emoji}</span>
              <span className="scratch__prize-label">{s.rarity}</span>
              <span className="scratch__prize-reward" style={{ display:'flex', alignItems:'center', gap:3 }}>
                +{s.reward.toLocaleString()}<STokenLogo size={12} />
              </span>
            </div>
          ))}
        </div>

        <button
          className="btn btn--primary btn--full"
          onClick={buyCard}
          disabled={purchasing}
          style={{ marginTop: 'var(--sp-3)' }}
        >
          {purchasing ? 'Purchasing…' : (
            <span style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'center' }}>
              Buy Card · {COST}<STokenLogo size={15} />
            </span>
          )}
        </button>
      </div>
    );
  }

  // ── SCRATCH / DONE SCREEN ───────────────────────────────────────────────────
  const allRevealed = revealed.size === 9;
  const isWin = grid?.totalReward > 0;
  const winSymbol = isWin ? SYMBOLS.find(s => s.reward === grid.totalReward) : null;

  return (
    <div className={`scratch${phase === 'done' ? (isWin ? ' scratch--win' : ' scratch--lose') : ''}`}>
      <h2 className="scratch__title">Scratch Card</h2>
      <p className="scratch__cost">
        {phase === 'scratching' && !allRevealed
          ? `${9 - revealed.size} left — tap to reveal`
          : phase === 'done'
          ? 'Card complete'
          : 'Revealing…'}
      </p>

      <div className="scratch__grid">
        {(grid?.cells || []).map((cell, i) => {
          const isRevealed = revealed.has(i);
          const isWinner   = winCells.has(i);
          return (
            <button
              key={i}
              className={`scratch__cell${isRevealed ? ' scratch__cell--revealed' : ' scratch__cell--hidden'}${isWinner ? ' scratch__cell--winner' : ''}`}
              style={{ '--sym-color': cell.color, '--sym-glow': cell.glowColor }}
              onClick={() => revealCell(i)}
              disabled={isRevealed || phase === 'done'}
              aria-label={isRevealed ? cell.label : 'Hidden — tap to reveal'}
            >
              {isRevealed ? (
                <div className="scratch__cell-inner">
                  <span className="scratch__cell-emoji">{cell.emoji}</span>
                  <span className="scratch__cell-label">{cell.label}</span>
                </div>
              ) : (
                <span className="scratch__cell-q">?</span>
              )}
            </button>
          );
        })}
      </div>

      {phase === 'done' && (
        <div className={`scratch__result${isWin ? ' scratch__result--win' : ' scratch__result--lose'}`}>
          {isWin ? (
            <>
              {winSymbol && <span style={{ fontSize: 24 }}>{winSymbol.emoji}</span>}
              <STokenLogo size={22} />
              <span>+{grid.totalReward.toLocaleString()}!</span>
            </>
          ) : (
            <span>Better luck next time</span>
          )}
        </div>
      )}

      {phase === 'scratching' && (
        <button className="btn btn--ghost btn--full" onClick={revealAll}>
          Reveal All
        </button>
      )}

      {phase === 'done' && (
        <button className="btn btn--primary btn--full" onClick={newCard}>
          <span style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'center' }}>
            New Card · {COST}<STokenLogo size={15} />
          </span>
        </button>
      )}
    </div>
  );
}
